import { create } from 'zustand';

import { db } from '@/services/storage/db';
import { HuntService } from '@/services/hunt-service';
import { useAuthStore } from '@/stores/auth-store';
import { posthogService } from '@/services/posthog-service';
import { EVENT_HUNT_STARTED } from '@/services/observability/events';
import type { Hunt, HuntSit } from '@/types';

const huntService = new HuntService(db);

export interface CreateHuntInput {
  seasonId: string;
  propertyId: string;
  name: string;
  startDate: string;
  endDate: string;
  companions: string[];
}

export interface CreateSitInput {
  huntId: string;
  waypointId: string;
  date: string;
  period: 'morning' | 'evening' | 'all-day';
  timeIn?: string;
  timeOut?: string;
  notes?: string;
}

interface HuntStore {
  hunts: Hunt[];
  currentHunt: Hunt | null;
  sits: HuntSit[];
  /** Per-hunt sit counts, keyed by huntId. Populated by loadHuntsForSeason. */
  sitCountsByHuntId: Record<string, number>;
  isLoading: boolean;
  error: string | null;

  // Hunt CRUD
  loadHuntsForSeason: (seasonId: string) => Promise<void>;
  loadHunt: (huntId: string) => Promise<void>;
  createHunt: (data: CreateHuntInput) => Promise<Hunt>;
  updateHunt: (huntId: string, data: Partial<Hunt>) => Promise<void>;
  deleteHunt: (huntId: string) => Promise<void>;

  // Sit operations (inline on hunt detail — no separate route)
  loadSitsForHunt: (huntId: string) => Promise<void>;
  createSit: (data: CreateSitInput) => Promise<HuntSit>;
  updateSit: (sitId: string, data: Partial<HuntSit>) => Promise<void>;
  deleteSit: (sitId: string) => Promise<void>;

  // Helpers
  reset: () => void;
}

const INITIAL_STATE = {
  hunts: [] as Hunt[],
  currentHunt: null as Hunt | null,
  sits: [] as HuntSit[],
  sitCountsByHuntId: {} as Record<string, number>,
  isLoading: false,
  error: null as string | null,
};

export const useHuntStore = create<HuntStore>((set) => ({
  ...INITIAL_STATE,

  loadHuntsForSeason: async (seasonId) => {
    set({ isLoading: true, error: null });
    try {
      const hunts = await db.getHuntsBySeason(seasonId);
      const sorted = [...hunts].sort((a, b) => b.startDate.localeCompare(a.startDate));
      // Load sit counts for each hunt in parallel
      const sitCountEntries = await Promise.all(
        sorted.map(async (hunt) => {
          const sits = await db.getHuntSitsByHunt(hunt.id);
          return [hunt.id, sits.length] as [string, number];
        }),
      );
      const sitCountsByHuntId = Object.fromEntries(sitCountEntries);
      set({ hunts: sorted, sitCountsByHuntId, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load hunts';
      console.error('HuntStore.loadHuntsForSeason:', err);
      set({ error: message, isLoading: false });
    }
  },

  loadHunt: async (huntId) => {
    set({ isLoading: true, error: null });
    try {
      const hunt = await db.getHunt(huntId);
      set({ currentHunt: hunt, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load hunt';
      console.error('HuntStore.loadHunt:', err);
      set({ error: message, isLoading: false });
    }
  },

  createHunt: async (data) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId) {
      throw new Error('[hunt-store] createHunt: cannot create — user not authenticated');
    }
    try {
      const hunt = await db.createHunt({
        ...data,
        userId,
        photos: [],
      });
      set((state) => ({
        hunts: [hunt, ...state.hunts],
      }));
      posthogService.track(EVENT_HUNT_STARTED);
      return hunt;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create hunt';
      console.error('HuntStore.createHunt:', err);
      set({ error: message });
      throw err;
    }
  },

  updateHunt: async (huntId, data) => {
    try {
      const updated = await db.updateHunt(huntId, data);
      set((state) => ({
        hunts: state.hunts.map((h) => (h.id === huntId ? updated : h)),
        currentHunt: state.currentHunt?.id === huntId ? updated : state.currentHunt,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update hunt';
      console.error('HuntStore.updateHunt:', err);
      set({ error: message });
      throw err;
    }
  },

  deleteHunt: async (huntId) => {
    try {
      // Route through HuntService so the soft-delete is enqueued for sync
      // (recordPendingDelete) — db.deleteHunt alone is a hard local delete that
      // never propagates deleted_at, causing cross-device resurrection (ADR-019).
      await huntService.deleteHunt(huntId);
      set((state) => ({
        hunts: state.hunts.filter((h) => h.id !== huntId),
        currentHunt: state.currentHunt?.id === huntId ? null : state.currentHunt,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete hunt';
      console.error('HuntStore.deleteHunt:', err);
      set({ error: message });
      throw err;
    }
  },

  loadSitsForHunt: async (huntId) => {
    set({ isLoading: true, error: null });
    try {
      const sits = await db.getHuntSitsByHunt(huntId);
      const sorted = [...sits].sort((a, b) => a.date.localeCompare(b.date));
      set({ sits: sorted, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load sits';
      console.error('HuntStore.loadSitsForHunt:', err);
      set({ error: message, isLoading: false });
    }
  },

  createSit: async (data) => {
    const userId = useAuthStore.getState().user?.id;
    if (!userId) {
      throw new Error('[hunt-store] createSit: cannot create — user not authenticated');
    }

    // Validate timeOut > timeIn when both are set
    if (data.timeIn && data.timeOut && data.timeOut <= data.timeIn) {
      throw new Error('Time out must be after time in');
    }

    try {
      const sit = await db.createHuntSit({
        ...data,
        userId,
        observations: [],
      });
      set((state) => ({
        sits: [...state.sits, sit].sort((a, b) => a.date.localeCompare(b.date)),
        sitCountsByHuntId: {
          ...state.sitCountsByHuntId,
          [data.huntId]: (state.sitCountsByHuntId[data.huntId] ?? 0) + 1,
        },
      }));
      return sit;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create sit';
      console.error('HuntStore.createSit:', err);
      set({ error: message });
      throw err;
    }
  },

  updateSit: async (sitId, data) => {
    try {
      const updated = await db.updateHuntSit(sitId, data);
      set((state) => ({
        sits: state.sits.map((s) => (s.id === sitId ? updated : s)),
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update sit';
      console.error('HuntStore.updateSit:', err);
      set({ error: message });
      throw err;
    }
  },

  deleteSit: async (sitId) => {
    try {
      await db.deleteHuntSit(sitId);
      set((state) => {
        const deletedSit = state.sits.find((s) => s.id === sitId);
        const huntId = deletedSit?.huntId;
        const updatedCounts = huntId
          ? {
              ...state.sitCountsByHuntId,
              [huntId]: Math.max(0, (state.sitCountsByHuntId[huntId] ?? 0) - 1),
            }
          : state.sitCountsByHuntId;
        return {
          sits: state.sits.filter((s) => s.id !== sitId),
          sitCountsByHuntId: updatedCounts,
        };
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete sit';
      console.error('HuntStore.deleteSit:', err);
      set({ error: message });
      throw err;
    }
  },

  reset: () => set(INITIAL_STATE),
}));
